import { AccountTypeNumber } from '~/types';
import { SmartPayPaymentExtendedDetailsModel, SmartPayPaymentExtendedModel, Supplier } from '../../../../../types/internal-api';

export enum SmartPayCustomerType {
    NewCustomer = 1,
    ExistingCustomerWithIntegration = 2,
    ExistingCustomerWithoutIntegration = 3,
}

export enum SmartPayOnboardingStatus {
    // User has no N1 integration added
    Unregistered = 1,
    // User has added the integration and stored their settings (journal, bank account), awaiting N1 email
    Pending = 2,
    // User has used their one-time password and begun onboarding
    CustomerVerifyingData = 3,
    // New customer type clicked continue onboarding while he was in CustomerVerifyingData state
    ContinuedOnboarding = 4,
    AwaitingCustomerInformation = 10,
    // User has completed N1 onboarding and is awaiting validation and approval by N1 and NETS
    CustomerReadyForValidation = 5,
    CustomerReadyForApproval = 6,
    // User has been validated and approved by N1 and NETS and can now make payments
    Completed = 7,
    // failure states
    Rejected = 8,
    Blocked = 9,
}

export enum SmartPayIntegrationError {
    // error codes also act as translation keys
    SMARTPAY_ONBOARDING_MODAL_ERROR_STATE_DEFAULT_MESSAGE = 'SMARTPAY_ONBOARDING_MODAL_ERROR_STATE_DEFAULT_MESSAGE',
    SMARTPAY_ONBOARDING_MODAL_ERROR_STATE_ACCOUNT_NUMBER_LENGTH_INVALID = 'SMARTPAY_ONBOARDING_MODAL_ERROR_STATE_ACCOUNT_NUMBER_LENGTH_INVALID',
    SMARTPAY_ONBOARDING_MODAL_ERROR_STATE_BANK_NOT_SUPPORTED = 'SMARTPAY_ONBOARDING_MODAL_ERROR_STATE_BANK_NOT_SUPPORTED',
    NEWSLETTER_SUBSCRIBE = 'NEWSLETTER_SUBSCRIBE',
}

export interface SmartPayIntegrationModel {
    refetchIntegrationState: () => Promise<void>;
    daybook?: number;
    paymentAccount?: number;
    feeAccount?: number;
    novemberFirstCustomerNumber: string;
    cvr: string;
    contact: string;
    telephone: string;
    email: string;
    customerType?: SmartPayCustomerType;
    status: SmartPayOnboardingStatus;
    onboardingNfUrl: string;
    lastUpdatedDate?: Date;
    autoWithdrawActiveDate?: Date;
    subscriptionType: 0 | 1 | 2;
    unsubscriptionDate?: Date;
    isPaymentCreationEnabled: boolean;
}

export const SMARTPAY_SIGNUP_STEPS = Object.freeze({
    CONSENT: 'CONSENT',
    SELECT_PLAN: 'SELECT_PLAN',
    COMPANY_DATA: 'COMPANY_DATA',
    USER_DATA: 'USER_DATA',
    PAYMENT_DATA: 'PAYMENT_DATA',
    INFORMATION_SENT: 'INFORMATION_SENT',
});

export const SMARTPAY_PLAN_OPTIONS = [
    {
        id: 1,
        name: 'SMARTPAY_ONBOARDING_MODAL_PLAN_OPTION_STANDARD_NAME',
        price: 'SMARTPAY_ONBOARDING_MODAL_PLAN_OPTION_STANDARD_PRICE',
        features: 'SMARTPAY_ONBOARDING_MODAL_PLAN_OPTION_STANDARD_FEATURES',
    },
    {
        id: 2,
        name: 'SMARTPAY_ONBOARDING_MODAL_PLAN_OPTION_SUBSCRIPTION_NAME',
        price: 'SMARTPAY_ONBOARDING_MODAL_PLAN_OPTION_SUBSCRIPTION_PRICE',
        features: 'SMARTPAY_ONBOARDING_MODAL_PLAN_OPTION_SUBSCRIPTION_FEATURES',
    },
];

// PAYMENTS

export interface SuppliersResponse {
    pagination: PaginationResponse;
    data: Supplier[];
}
export interface PaymentsResponse {
    pagination: PaginationResponse;
    data: SmartPayPaymentExtendedModel[];
}

export interface PaginationResponse {
    count: number;
    pageSize: number;
    returnAll: boolean;
    skipPages: number;
}

export interface SmartPayPaymentsFilters {
    fromDate: Date;
    toDate: Date;
    supplier: string;
    supplierGroup: string;
    status: string;
    includeDaybooks: boolean;
    senderBankAccount: string;
    journalIds?: string;
}

export interface SmartPayPaymentsDefaultFilters {
    filter: SmartPayPaymentsFilters;
    yearSpecialPeriodName: string;
}

export interface SmartPayPaymentsSettings {
    combineInvoices?: boolean;
    paymentShouldArriveOnDueDate?: boolean;
}
export interface SmartPayIntegrationSettings {
    PaymentAccount?: number | null;
    Daybook?: number | null;
    FeeAccount?: number | null;
}
export interface SmartPayHandledPayment extends SmartPayPaymentExtendedModel, SmartPayPaymentExtendedDetailsModel {}

// internnal-api type generator doesn't pick this one, creating it manually for now
export interface SmartPayBankAccountDto {
    bankName: string;
    bankAccount: string;
}
export enum SmartPayPaymentExcludeType {
    Remove = 0,
    MarkedAsPaid = 1,
}
export enum SmartPayPaymentAccountStatus {
    NotOnNovemberFirst = 'NotOnNovemberFirst',
    ActiveOS = 'ActiveOS',
    AppliedOS = 'AppliedOS',
    InactiveOS = 'InactiveOS',
}
export enum PaymentStatuses {
    New = 'NEW',
    Creating = 'CREATING',
    Pending = 'PENDING',
    PendingApproval = 'PENDING_APPROVAL',
    PartiallyApproved = 'PARTIALLY_APPROVED',
    Rejected = 'REJECTED',
    Scheduled = 'SCHEDULED',
    Processing = 'PROCESSING',
    TradeReverted = 'TRADE_REVERTED',
    TransferForbidden = 'TRANSFER_FORBIDDEN',
    Canceled = 'CANCELLED',
    Invalid = 'INVALID',
    Failed = 'FAILED',
    Completed = 'COMPLETED',
    CreditNote = 'CREDIT_NOTE',
    MissingEntries = 'MISSING_ENTRIES',
    MarkedAsPaid = 'MARKED_AS_PAID',
}

export const UNPAID_PAYMENTS_STATUSES = [
    PaymentStatuses.New,
    PaymentStatuses.Creating,
    PaymentStatuses.Rejected,
    PaymentStatuses.TradeReverted,
    PaymentStatuses.TransferForbidden,
    PaymentStatuses.Canceled,
    PaymentStatuses.Failed,
    PaymentStatuses.Invalid,
    PaymentStatuses.CreditNote,
];

export const PENDING_APPROVAL_PAYMENTS_STATUSES = [
    PaymentStatuses.Pending,
    PaymentStatuses.PendingApproval,
    PaymentStatuses.PartiallyApproved,
];

export const SCHEDULED_PAYMENTS_STATUSES = [PaymentStatuses.Scheduled, PaymentStatuses.Processing];
export const PAID_PAYMENTS_STATUSES = [PaymentStatuses.Completed, PaymentStatuses.MissingEntries, PaymentStatuses.MarkedAsPaid];

export const TABLE_SETTINGS_LOCALSTORAGE_KEY = 'smartpay-payments-table-settings';
export const PENDING_TABLE_SETTINGS_LOCALSTORAGE_KEY = 'smartpay-pending-payments-table-settings';
export const SCHEDULED_TABLE_SETTINGS_LOCALSTORAGE_KEY = 'smartpay-scheduled-payments-table-settings';
export const PAID_TABLE_SETTINGS_LOCALSTORAGE_KEY = 'smartpay-paid-payments-table-settings';
export const ORPHANED_TABLE_SETTINGS_LOCALSTORAGE_KEY = 'smartpay-orphaned-payments-table-settings';
export const MATCH_PAYMENT_CREDIT_NOTE_LOCALSTORAGE_KEY = 'smartpay-match-payment-credit-note-table-settings-adjusted';
export enum SmartpaySettingsModalTabs {
    Setup = 'Setup',
    BookKeeping = 'BookKeeping',
    BankAccounts = 'BankAccounts',
    MySetup = 'MySetup',
    Users = 'Users',
    Approvals = 'Approvals',
}

export enum SmartPayUserRoles {
    PaymentCreator = 'PAYMENT_CREATOR',
    PaymentApprover = 'PAYMENT_APPROVER',
}

export interface RejectPayload {
    paymentReference: string;
    rejectReason: string | undefined;
}

export interface SmartPayRemovePayload {
    journalId: number | undefined;
    draftEntryId: number | undefined;
    bookedEntryId: number | undefined;
    excludeType: SmartPayPaymentExcludeType;
}

export type SmartPayPaymentExtendedModelWithRejectionReason = SmartPayPaymentExtendedModel & {
    reasonForRejection?: string;
};

export const TABLE_PAGE_SIZE = 50;

export const CREATE_PAYMENTS_BATCH_SIZE = 10;

export const EXCLUDE_PAYMENTS_BATCH_SIZE = 5;

export const NonDKKorEURpaymentFee = 140;

export interface CountAndTotal {
    count: number;
    total: number;
}

export const RETURN_FROM_NF_APPROVAL_FLOW_URL_PARAM = 'OfCourseIStillLoveYou';
export const SMARTPAY_WOOTRIC_SURVEY_LOCAL_STORAGE_KEY = 'smartpay-native-has-wootric-survery-been-shown';
export interface ExcludePayments {
    rows: SmartPayPaymentExtendedModel[];
    type: SmartPayPaymentExcludeType;
}

export interface SmartPayPaymentExtendedModelWithDuplicates extends SmartPayPaymentExtendedModel {
    suspectedDuplicatesReferences: string[];
}

export interface SmartPayCreatePaymentPayload {
    journalEntryPaymentId: number;
    amount: number;
    paymentAccountId?: number;
    coverRecipientFee?: boolean;
    notifyApprovers?: boolean;
}

export const smartPayValidAccountTypes: AccountTypeNumber[] = [AccountTypeNumber.ProfitAndLoss, AccountTypeNumber.BalanceSheet];
